import React from 'react';
import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
    Tooltip as ReTooltip,
    AreaChart,
    Area,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    TooltipProps,
} from 'recharts';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { Asset } from '../types';

interface AllocationChartProps {
  assets: Asset[];
}

const COLORS = ['#38BDF8', '#0EA5E9', '#0284C7', '#0369A1', '#7DD3FC', '#1D4ED8', '#60A5FA', '#94A3B8'];

export const AllocationChart: React.FC<AllocationChartProps> = ({ assets }) => {
  const rawData = assets.reduce((acc, asset) => {
    const value = asset.quantity * asset.currentPrice;
    if (!Number.isFinite(value) || value <= 0) return acc;

    const key = asset.sector && asset.sector !== 'Unclassified' ? asset.sector : asset.symbol;
    const existing = acc.find(item => item.name === key);
    if (existing) {
      existing.value += value;
    } else {
      acc.push({ name: key, value });
    }
    return acc;
  }, [] as { name: string; value: number }[]).sort((a, b) => b.value - a.value);

  const total = rawData.reduce((sum, item) => sum + item.value, 0);
  const visibleData = rawData.filter((item) => total > 0 && item.value / total >= 0.01);
  const otherValue = rawData
    .filter((item) => total > 0 && item.value / total < 0.01)
    .reduce((sum, item) => sum + item.value, 0);
  const cappedVisibleData = visibleData.slice(0, 8);
  const overflowValue = visibleData.slice(8).reduce((sum, item) => sum + item.value, 0);
  const groupedOtherValue = otherValue + overflowValue;
  const data = groupedOtherValue > 0 ? [...cappedVisibleData, { name: 'Other', value: groupedOtherValue }] : cappedVisibleData;

  if (!data.length) {
    return (
      <div className="flex min-h-[340px] items-center justify-center rounded-xl border border-dashed border-white/10 bg-[#15151c] px-6 text-center">
        <div>
          <p className="text-sm font-semibold text-slate-200">No allocation data yet</p>
          <p className="mt-1 text-xs text-slate-500">Import holdings to see your portfolio mix.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-[320px] w-full flex-col justify-between overflow-hidden">
      <div className="h-48 w-full min-w-0 sm:h-52">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius="58%"
              outerRadius="78%"
              paddingAngle={3}
              dataKey="value"
              stroke="#1e1e26"
              strokeWidth={2}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${entry.name}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <ReTooltip
              formatter={(value: number, name: string) => [`£${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`, name]}
              contentStyle={{ backgroundColor: '#1e1e26', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', maxWidth: 260 }}
              itemStyle={{ color: '#fff' }}
              wrapperStyle={{ zIndex: 30, pointerEvents: 'none' }}
              allowEscapeViewBox={{ x: true, y: true }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 grid max-h-36 grid-cols-1 gap-2 overflow-hidden sm:grid-cols-2">
        {data.map((entry, index) => {
          const pct = total > 0 ? (entry.value / total) * 100 : 0;
          return (
            <div key={entry.name} className="flex min-w-0 items-center justify-between gap-2 rounded-lg bg-white/[0.03] px-3 py-2 text-xs text-slate-400">
              <div className="flex min-w-0 items-center gap-2">
                <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                <span className="truncate" title={entry.name}>{entry.name}</span>
              </div>
              <span className="flex-shrink-0 font-semibold text-slate-200">{pct.toFixed(pct < 10 ? 1 : 0)}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

interface PerformanceChartProps {
    data?: any[];
    color?: string;
    currencySymbol?: string;
}

export const PerformanceChart: React.FC<PerformanceChartProps> = ({ data, color = "#6366f1", currencySymbol = "£" }) => {
    const defaultData = [
        { name: 'Jan', value: 38000 },
        { name: 'Feb', value: 40000 },
        { name: 'Mar', value: 39500 },
        { name: 'Apr', value: 43000 },
    ];
    const chartData = data && data.length > 0 ? data : defaultData;
    const safeColorId = String(color).replace('#', '');

    return (
        <div className="h-72 w-full">
             <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                data={chartData}
                margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                >
                <defs>
                    <linearGradient id={`colorValue-${safeColorId}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={color} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={color} stopOpacity={0}/>
                    </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 11}} dy={10} minTickGap={30} />
                <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{fill: '#64748b', fontSize: 11}}
                    tickFormatter={(val) => val >= 1000 ? `${currencySymbol}${val/1000}k` : `${currencySymbol}${val}`}
                />
                <ReTooltip
                    contentStyle={{ backgroundColor: '#1e1e26', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }}
                    cursor={{ stroke: 'rgba(255,255,255,0.1)', strokeWidth: 1 }}
                    formatter={(value: number) => [value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }), 'Value']}
                />
                <Area type="monotone" dataKey="value" stroke={color} strokeWidth={3} fillOpacity={1} fill={`url(#colorValue-${safeColorId})`} />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    )
}

interface DividendChartDatum {
    name: string;
    value: number;
    dividends: { symbol: string; amount: number }[];
}

interface DividendChartProps {
    data: DividendChartDatum[];
    color?: string;
    currency?: string;
}

export const DividendChart: React.FC<DividendChartProps> = ({ data, color = "#38BDF8", currency = "£" }) => {
    const hasHistory = data.some((item) => item.value > 0);

    if (!hasHistory) {
        return (
            <div className="flex h-56 w-full items-center justify-center rounded-xl border border-dashed border-white/10 bg-[#15151c] px-6 text-center">
                <div>
                    <p className="text-sm font-semibold text-slate-200">No dividend history yet</p>
                    <p className="mt-1 text-xs text-slate-500">Received dividends will appear here once recorded.</p>
                </div>
            </div>
        );
    }

    const chartData = data;
    const barSize = Math.max(10, Math.min(28, Math.floor(280 / chartData.length)));

    const CustomTooltip = ({ active, payload }: TooltipProps<ValueType, NameType>) => {
        if (!active || !payload?.length) return null;

        const monthData = payload[0].payload as DividendChartDatum;
        const hasDividends = monthData.dividends.length > 0;

        return (
            <div className="max-w-[260px] bg-[#1e1e26] border border-white/10 p-3 rounded-lg shadow-xl">
                <p className="text-slate-300 text-xs mb-1">{monthData.name}</p>
                <p className="text-white font-bold">
                    {currency}
                    {monthData.value.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    })}
                </p>
                <p className="text-[10px] uppercase tracking-wide text-slate-500 mt-2">Received</p>
                <div className="mt-2 space-y-1 max-h-40 overflow-y-auto pr-1">
                    {hasDividends ? (
                        monthData.dividends.map((dividend, index) => (
                            <div key={`${dividend.symbol}-${index}`} className="flex items-center justify-between text-xs text-slate-200">
                                <div className="flex items-center space-x-2">
                                    <span className="h-2 w-2 rounded-full bg-sky-400" />
                                    <span className="font-medium">{dividend.symbol}</span>
                                </div>
                                <span className="text-slate-300">
                                    {currency}
                                    {dividend.amount.toFixed(2)}
                                </span>
                            </div>
                        ))
                    ) : (
                        <p className="text-[11px] text-slate-500">No dividends this month</p>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 0, left: -25, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                    <XAxis
                        dataKey="name"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#64748b', fontSize: 11 }}
                        dy={10}
                    />
                    <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#64748b', fontSize: 11 }}
                        tickFormatter={(val) => `${currency}${val}`}
                    />
                    <ReTooltip cursor={{ fill: 'rgba(255,255,255,0.05)' }} content={<CustomTooltip />} wrapperStyle={{ zIndex: 30, pointerEvents: 'none' }} allowEscapeViewBox={{ x: true, y: true }} />
                    <Bar dataKey="value" fill={color} radius={[4, 4, 0, 0]} barSize={barSize} />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};
